import { useState } from "react";
import { ImagePlus, X, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { isDng, dngPreview } from "@/lib/dng";

const MAX = 5;
const SIZE = 1400;
const isHeic = (f) => /hei[cf]/i.test(f.type) || /\.(heic|heif)$/i.test(f.name);

// orientation: tag TIFF (6 = girar 90° horário, 8 = 90° anti-horário, 3 = 180°) para JPEGs sem EXIF próprio.
const resize = (file, orientation = 1) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode")); };
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, SIZE / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const turn = orientation === 6 || orientation === 8;
      const canvas = document.createElement("canvas");
      canvas.width = turn ? h : w;
      canvas.height = turn ? w : h;
      const ctx = canvas.getContext("2d");
      if (orientation === 6) { ctx.translate(h, 0); ctx.rotate(Math.PI / 2); }
      if (orientation === 8) { ctx.translate(0, w); ctx.rotate(-Math.PI / 2); }
      if (orientation === 3) { ctx.translate(w, h); ctx.rotate(Math.PI); }
      ctx.drawImage(img, 0, 0, w, h);
      const out = canvas.toDataURL("image/jpeg", 0.82);
      if (out.length < 100) reject(new Error("canvas"));
      else resolve(out);
    };
    img.src = url;
  });

const convertOnServer = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/images/convert", fd);
  return data.data_url;
};

const toDataUrl = async (file) => {
  if (isDng(file)) {
    try {
      const { blob, orientation } = await dngPreview(file);
      return await resize(blob, orientation);
    } catch (e) {
      if (file.size <= 30 * 1024 * 1024) return convertOnServer(file);
      throw e;
    }
  }
  if (isHeic(file)) return convertOnServer(file);
  try {
    return await resize(file);
  } catch {
    return convertOnServer(file);
  }
};

export const ImagePicker = ({ value = [], onChange }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX - value.length);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    const results = await Promise.allSettled(files.map(toDataUrl));
    setBusy(false);
    const ok = results.filter((r) => r.status === "fulfilled").map((r) => r.value);
    const failed = results.find((r) => r.status === "rejected");
    if (ok.length) onChange([...value, ...ok]);
    if (failed) toast.error(`Não foi possível adicionar ${results.length - ok.length} foto(s). ${formatApiError(failed.reason)}`);
  };
  const remove = (i) => onChange(value.filter((_, j) => j !== i));
  const makeCover = (i) => onChange([value[i], ...value.filter((_, j) => j !== i)]);

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {value.map((src, i) => (
          <div key={i} data-testid={`product-image-thumb-${i}`} className={`relative h-24 w-20 overflow-hidden rounded-xl border ${i === 0 ? "border-white" : "border-white/10"}`}>
            <img src={src} alt={`Foto ${i + 1}`} className="h-full w-full object-cover" />
            {i === 0 ? (
              <span className="absolute bottom-1 left-1 rounded-full bg-white px-1.5 text-[10px] font-bold text-black">Capa</span>
            ) : (
              <button type="button" data-testid={`product-image-cover-${i}`} onClick={() => makeCover(i)} title="Usar como capa" className="absolute bottom-1 left-1 rounded-full bg-black/80 p-1"><Star className="h-3 w-3" /></button>
            )}
            <button type="button" data-testid={`product-image-remove-${i}`} onClick={() => remove(i)} className="absolute right-1 top-1 rounded-full bg-black/80 p-1" aria-label="Remover foto"><X className="h-3 w-3" /></button>
          </div>
        ))}
        {value.length < MAX && (
          <label className={`flex h-24 w-20 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-white/20 text-zinc-400 transition-colors ${busy ? "pointer-events-none" : "cursor-pointer hover:border-white/50 hover:text-white"}`}>
            {busy ? <Loader2 data-testid="product-image-loading" className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
            <span className="text-[11px]">{busy ? "Enviando…" : "Adicionar"}</span>
            <input data-testid="product-image-input" type="file" accept="image/*,.heic,.heif,.webp,.dng" multiple disabled={busy} onChange={pick} className="hidden" />
          </label>
        )}
      </div>
      <p className="mt-2 text-xs text-zinc-400">Até {MAX} fotos (JPG, PNG, WEBP, HEIC ou DNG/ProRAW do iPhone). A primeira é a capa; toque na estrela para trocar.</p>
    </div>
  );
};
